import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import ScrollArrow from '../components/ScrollArrow';
import LogoAnimation from '../components/LogoAnimation';
import TypingText from '../components/TypingText';
import {projects} from '../data/projectsData';
import './Home.css';

function Home() {
  const [typingComplete, setTypingComplete] = useState(false);
  const aboutRef = useRef(null);
  const isAboutInView = useInView(aboutRef, { once: true, margin: "-100px" });
  const featuredProjects = projects.slice(0, 3).map(project => ({
    id: project.id,
    title: project.title,
    image: project.coverImage,
    context: project.context
  }));

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setTypingComplete(true);
    }, 1800);
    return () => clearTimeout(timer);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  return (
    <motion.div 
      className="home"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="home-hero">
        <LogoAnimation isVisible={typingComplete} />
        <motion.h1 
          className={`home-name typing-animation ${typingComplete ? 'typing-complete' : ''}`}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
        >
          Marie Mercier
        </motion.h1>
        <motion.p 
          className="home-role"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: [0.4, 0, 0.2, 1] }}
        >
          Directrice Artistique • Designer graphique
        </motion.p>
        <motion.p 
          className="home-manifesto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2, ease: [0.4, 0, 0.2, 1] }}
        >
          Je crée des identités visuelles<br />épurées et sensibles<span className="accent-dot">.</span>
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.6, ease: [0.4, 0, 0.2, 1] }}
        >
          <Link to="/portfolio" className="home-cta">
            Voir le portfolio
          </Link>
        </motion.div>
        <ScrollArrow />
      </div>
      <motion.div 
        className="home-featured"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
      >
        <div className="home-featured-grid">
          {featuredProjects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ 
                duration: 1, 
                delay: index * 0.2,
                ease: [0.4, 0, 0.2, 1]
              }}
            >
              <Link 
                to={`/project/${project.id}`}
                className="home-featured-item"
              >
                <motion.div 
                  className="home-featured-image-wrapper"
                  whileHover={{ scale: 1.03 }}
                  transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                >
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="home-featured-image"
                  />
                </motion.div>
                <div className="home-featured-info">
                  <h3 className="home-featured-title">{project.title}</h3>
                  <p className="home-featured-context">{project.context}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>
      <motion.div 
        ref={aboutRef}
        className="home-about"
        initial={{ opacity: 0, y: 40 }}
        animate={isAboutInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
        transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
      >
        <div className="home-about-content">
          <p className="home-about-text">
            {isAboutInView && (
              <TypingText 
                text="Chaque projet est une rencontre entre esthétique et sens, où la simplicité révèle l'essentiel."
                speed={50}
                delay={300}
              />
            )}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default Home;

